import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IndianRupee,
  TrendingUp,
  ArrowLeft,
  AlertTriangle,
  ChevronRight,
  CheckCircle,
  Clock,
  XCircle,
  School,
  Package,
} from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/pmshribanner.webp';

import {
  UtilizationGauge,
  TrendLineChart,
} from '../components/charts';

import { YearDropdown, PhaseDropdown } from '../components/filters';

import type {
  Activity,
  SubActivity,
  MonthlyTrendData,
  SanctionStatus,
  ExpenditureActivityTreeNode,
} from '../types';
import { useGetExpenditureActivityTreeQuery } from '../services/api';

type StatusFilter = 'all' | 'fully_utilized' | 'partially_utilized' | 'not_utilized' | 'pending';

const ActivityAnalyticsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { headCode, subheadId, activityId } = useParams<{ headCode: string; subheadId: string; activityId: string }>();
  const carouselImages = [img1];

  // State
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<'name' | 'utilization' | 'sanctioned'>('name');
  const [selectedPhase, setSelectedPhase] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);

  // Get current year for display (fallback to current fiscal year)
  const currentYear = selectedYear || new Date().getFullYear();

  // Fetch expenditure activity tree from API (provides sanTotal, spentTotal, sanRec, sanNonRec, spentRec, spentNonRec)
  const { data: expenseTreeData, isLoading } = useGetExpenditureActivityTreeQuery({
    ...(selectedYear !== null && { year: selectedYear }),
    ...(selectedPhase !== null && { phase: selectedPhase }),
  });

  // Find head, subhead, and activity from API data
  const { headFromAPI, subheadFromAPI, activityFromAPI } = useMemo(() => {
    if (!expenseTreeData?.success || !expenseTreeData?.data?.tree || !headCode || !subheadId || !activityId) {
      return { headFromAPI: null, subheadFromAPI: null, activityFromAPI: null };
    }

    const headNumber = parseInt(headCode.replace('H', ''));
    const subheadNumber = parseInt(subheadId);
    const activityNumber = parseInt(activityId);

    // Find head by id or index
    const head = expenseTreeData.data.tree.find((h: ExpenditureActivityTreeNode, idx: number) => h.id === headNumber || idx === headNumber - 1);
    if (!head) return { headFromAPI: null, subheadFromAPI: null, activityFromAPI: null };

    // Find subhead by id
    const subhead = head.children?.find((sh: ExpenditureActivityTreeNode) => sh.id === subheadNumber);
    if (!subhead) return { headFromAPI: head, subheadFromAPI: null, activityFromAPI: null };

    // Find activity by id
    const activity = subhead.children?.find((a: ExpenditureActivityTreeNode) => a.id === activityNumber);

    return { headFromAPI: head, subheadFromAPI: subhead, activityFromAPI: activity || null };
  }, [expenseTreeData, headCode, subheadId, activityId]);

  // Transform API data to Activity type
  const activity = useMemo<Activity | null>(() => {
    if (!activityFromAPI || !subheadFromAPI || !headFromAPI) return null;

    // Use actual values from Expenditure API
    const totalSanctioned = activityFromAPI.sanTotal || 0;
    const totalSpent = activityFromAPI.spentTotal || 0;
    const utilization = totalSanctioned > 0 ? (totalSpent / totalSanctioned) * 100 : 0;
    const totalUnspent = totalSanctioned - totalSpent;
    // Determine expenditure type based on sanRec vs sanNonRec
    const expenditureType = (activityFromAPI.sanRec || 0) > (activityFromAPI.sanNonRec || 0) ? 'recurring' : 'non-recurring';

    return {
      id: activityFromAPI.id,
      subheadId: subheadFromAPI.id,
      subheadName: subheadFromAPI.title,
      headId: headFromAPI.id,
      headName: headFromAPI.title,
      code: `H${headFromAPI.id}.${subheadFromAPI.id}.${activityFromAPI.id}`,
      name: activityFromAPI.title,
      description: `Activity under ${subheadFromAPI.title} for PM-SHRI schools`,
      expenditureType,
      totalSanctioned,
      totalReceived: totalSanctioned, // Using sanctioned as received for this API
      totalSpent,
      totalUnspent,
      utilizationPercentage: utilization,
      targetUnits: 20,
      completedUnits: 12,
      efficiencyPercentage: 75,
      subActivityCount: activityFromAPI.children?.length || 0,
    };
  }, [activityFromAPI, subheadFromAPI, headFromAPI]);

  // Transform subactivities from API
  const subActivities = useMemo<SubActivity[]>(() => {
    if (!activityFromAPI || !activity) return [];

    return (activityFromAPI.children || []).map((subAct: ExpenditureActivityTreeNode, idx: number) => {
      // Use actual values from Expenditure API
      const sanctionedAmount = subAct.sanTotal || 0;
      const spentAmount = subAct.spentTotal || 0;
      const utilization = sanctionedAmount > 0 ? (spentAmount / sanctionedAmount) * 100 : 0;
      const status: SanctionStatus = utilization >= 90 ? 'fully_utilized' :
        utilization >= 50 ? 'partially_utilized' :
          utilization > 0 ? 'not_utilized' : 'pending';

      return {
        id: subAct.id,
        activityId: activityFromAPI.id,
        activityName: activity.name,
        subheadId: activity.subheadId,
        subheadName: activity.subheadName,
        headId: activity.headId,
        headName: activity.headName,
        code: `${activity.code}.${subAct.id}`,
        name: subAct.title,
        description: `Sub-activity for ${subAct.title.toLowerCase()}`,
        expenditureType: activity.expenditureType,
        sanctionedAmount,
        spentAmount,
        unspentAmount: sanctionedAmount - spentAmount,
        utilizationPercentage: utilization,
        status,
        schoolId: 0,
        schoolName: '',
      };
    });
  }, [activityFromAPI, activity]);

  // Mock trends data
  const trends = useMemo<MonthlyTrendData[]>(() => {
    if (!activity) return [];

    const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
    return months.map((month, idx) => {
      const monthSanctioned = (activity.totalSanctioned / 12) * (0.8 + Math.random() * 0.4);
      const monthReceived = monthSanctioned * 0.75;
      const monthUtilization = 30 + (idx * 5);
      return {
        month,
        year: idx < 9 ? currentYear : currentYear + 1,
        sanctioned: monthSanctioned,
        received: monthReceived,
        spent: monthReceived * (monthUtilization / 100),
        utilization: monthUtilization,
        recurringSpent: monthReceived * 0.4 * (monthUtilization / 100),
        nonRecurringSpent: monthReceived * 0.6 * (monthUtilization / 100),
      };
    });
  }, [activity, currentYear]);

  // Head info for display
  const headInfo = useMemo(() => {
    if (!headFromAPI) return null;
    const colorArray = ['#6B1786', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6'];
    return {
      id: headFromAPI.id,
      code: `H${headFromAPI.id}`,
      name: headFromAPI.title,
      color: colorArray[(headFromAPI.id - 1) % colorArray.length],
    };
  }, [headFromAPI]);

  const formatCurrency = (value: number) => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const filteredSubActivities = subActivities
    .filter(sa => statusFilter === 'all' ? true : sa.status === statusFilter)
    .sort((a, b) => {
      if (sortBy === 'utilization') return b.utilizationPercentage - a.utilizationPercentage;
      if (sortBy === 'sanctioned') return b.sanctionedAmount - a.sanctionedAmount;
      return a.name.localeCompare(b.name);
    });

  const statusCounts = {
    fully_utilized: subActivities.filter(sa => sa.status === 'fully_utilized').length,
    partially_utilized: subActivities.filter(sa => sa.status === 'partially_utilized').length,
    not_utilized: subActivities.filter(sa => sa.status === 'not_utilized').length,
    pending: subActivities.filter(sa => sa.status === 'pending').length,
  };

  const getStatusIcon = (status: SanctionStatus) => {
    switch (status) {
      case 'fully_utilized':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'partially_utilized':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'not_utilized':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status: SanctionStatus) => {
    const styles = {
      fully_utilized: 'bg-green-100 text-green-700',
      partially_utilized: 'bg-yellow-100 text-yellow-700',
      not_utilized: 'bg-red-100 text-red-700',
      pending: 'bg-gray-100 text-gray-600',
    };
    const labels = {
      fully_utilized: 'Fully Utilized',
      partially_utilized: 'Partial',
      not_utilized: 'Not Utilized',
      pending: 'Pending',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/pm-shri/analytics/heads/${headCode}/subheads/${subheadId}`)}
              className="p-2 rounded-lg bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="px-3 py-1 rounded-lg text-white text-sm font-bold"
                  style={{ backgroundColor: headInfo?.color }}
                >
                  {headInfo?.name}
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 font-medium">{subheadFromAPI?.title}</span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">{activity?.name}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <YearDropdown value={selectedYear} onChange={setSelectedYear} />
            <PhaseDropdown value={selectedPhase} onChange={setSelectedPhase} />
          </div>
        </div>

        {/* Activity Info Card */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div className="flex-1 min-w-[300px]">
              <div className="flex items-center gap-3 mb-3">
                <Package className="w-6 h-6 text-purple-600" />
                <h2 className="text-lg font-semibold text-gray-900">Activity Details</h2>
              </div>
              <p className="text-gray-600 mb-4">{activity?.description}</p>
              <div className="flex flex-wrap gap-3">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                  activity?.expenditureType === 'recurring'
                    ? 'bg-cyan-100 text-cyan-700 border border-cyan-200'
                    : 'bg-rose-100 text-rose-700 border border-rose-200'
                }`}>
                  {activity?.expenditureType === 'recurring' ? '🔄 Recurring' : '💰 Non-Recurring'}
                </span>
                <span className="px-3 py-1.5 rounded-lg text-sm font-medium bg-purple-100 text-purple-700 border border-purple-200">
                  📦 {activity?.subActivityCount} Sub-Activities
                </span>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <UtilizationGauge value={activity?.utilizationPercentage || 0} size="lg" label="Utilization" />
            </div>
          </div>
        </div>

        {/* Financial Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <IndianRupee className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Sanctioned</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 mb-1">{formatCurrency(activity?.totalSanctioned || 0)}</div>
            <div className="text-xs text-gray-500">Total allocated budget</div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-green-100">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Spent</span>
            </div>
            <div className="text-2xl font-bold text-green-600 mb-1">{formatCurrency(activity?.totalSpent || 0)}</div>
            <div className="text-xs text-gray-500">
              {((activity?.totalSpent || 0) / (activity?.totalSanctioned || 1) * 100).toFixed(1)}% of sanctioned
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Unspent</span>
            </div>
            <div className="text-2xl font-bold text-red-600 mb-1">{formatCurrency(activity?.totalUnspent || 0)}</div>
            <div className="text-xs text-gray-500">Remaining balance</div>
          </div>
        </div>

        {/* Status Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Implementation Status Overview</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div
              onClick={() => setStatusFilter('fully_utilized')}
              className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${
                statusFilter === 'fully_utilized' 
                  ? 'border-green-500 bg-green-50 shadow-md' 
                  : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <CheckCircle className="w-10 h-10 text-green-500" />
                <div className="text-3xl font-bold text-green-600">{statusCounts.fully_utilized}</div>
                <div className="text-sm font-medium text-gray-700">Fully Utilized</div>
                <div className="text-xs text-gray-500">≥90% completion</div>
              </div>
            </div>
            <div
              onClick={() => setStatusFilter('partially_utilized')}
              className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${
                statusFilter === 'partially_utilized' 
                  ? 'border-yellow-500 bg-yellow-50 shadow-md' 
                  : 'border-gray-200 hover:border-yellow-300 hover:bg-yellow-50'
              }`}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <Clock className="w-10 h-10 text-yellow-500" />
                <div className="text-3xl font-bold text-yellow-600">{statusCounts.partially_utilized}</div>
                <div className="text-sm font-medium text-gray-700">Partial Progress</div>
                <div className="text-xs text-gray-500">50-89% completion</div>
              </div>
            </div>
            <div
              onClick={() => setStatusFilter('not_utilized')}
              className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${
                statusFilter === 'not_utilized' 
                  ? 'border-red-500 bg-red-50 shadow-md' 
                  : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
              }`}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <XCircle className="w-10 h-10 text-red-500" />
                <div className="text-3xl font-bold text-red-600">{statusCounts.not_utilized}</div>
                <div className="text-sm font-medium text-gray-700">Low Progress</div>
                <div className="text-xs text-gray-500">&lt;50% completion</div>
              </div>
            </div>
            <div
              onClick={() => setStatusFilter('pending')}
              className={`rounded-lg border-2 p-4 cursor-pointer transition-all ${
                statusFilter === 'pending' 
                  ? 'border-gray-500 bg-gray-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
              }`}
            >
              <div className="flex flex-col items-center text-center gap-2">
                <Clock className="w-10 h-10 text-gray-400" />
                <div className="text-3xl font-bold text-gray-600">{statusCounts.pending}</div>
                <div className="text-sm font-medium text-gray-700">Not Started</div>
                <div className="text-xs text-gray-500">Awaiting action</div>
              </div>
            </div>
          </div>
        </div>

        {/* Trend Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Expenditure Trend</h3>
          <TrendLineChart
            data={trends}
            height={300}
            showArea={true}
            dataKeys={['sanctioned', 'spent', 'utilization']}
          />
        </div>

        {/* Sub-Activities Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-1">
                <Package className="w-6 h-6 text-purple-600" />
                School-wise Implementation
              </h3>
              <p className="text-sm text-gray-500">
                Showing {filteredSubActivities.length} of {subActivities.length} sub-activities
              </p>
            </div>
            <div className="flex items-center gap-3">
              {statusFilter !== 'all' && (
                <button
                  onClick={() => setStatusFilter('all')}
                  className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-200 transition-colors"
                >
                  ✕ Clear Filter
                </button>
              )}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white hover:bg-gray-50 transition-colors"
              >
                <option value="name">📋 Sort by Name</option>
                <option value="utilization">📊 Sort by Utilization</option>
                <option value="sanctioned">💰 Sort by Amount</option>
              </select>
            </div>
          </div>

          {/* Sub-Activities Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-blue-50 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Sub-Activity</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Sanctioned</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">Spent</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Progress</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubActivities.map((subActivity) => (
                  <tr key={subActivity.id} className="hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          {getStatusIcon(subActivity.status)}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{subActivity.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{subActivity.code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="font-semibold text-gray-900">{formatCurrency(subActivity.sanctionedAmount)}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="font-semibold text-green-600">{formatCurrency(subActivity.spentAmount)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-full max-w-[120px] h-2.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              subActivity.utilizationPercentage >= 90 ? 'bg-green-500' :
                              subActivity.utilizationPercentage >= 75 ? 'bg-green-400' :
                              subActivity.utilizationPercentage >= 50 ? 'bg-yellow-500' : 
                              subActivity.utilizationPercentage > 0 ? 'bg-red-500' : 'bg-gray-300'
                            }`}
                            style={{ width: `${Math.min(subActivity.utilizationPercentage, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-bold text-gray-700">
                          {subActivity.utilizationPercentage.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {getStatusBadge(subActivity.status)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => navigate(`/pm-shri/analytics/heads/${headCode}/subheads/${subheadId}/activities/${activityId}/subactivities/${subActivity.id}`)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors shadow-sm"
                      >
                        <ChevronRight className="w-4 h-4" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ActivityAnalyticsScreen;
